import React, { useState } from 'react'
import cn from 'classnames'
import api from 'api/api'
import useStore from 'state/knovStore'
import type { Answer, PollOptionPayload } from 'types/answers'
import styles from './poll-answer.module.scss'

interface PollAnswerProps {
    answer: Answer
    isUserSignedIn: boolean
}

const formatExpiry = (rawValue?: string | null) => {
    if (!rawValue) return null
    const parsed = new Date(rawValue)
    if (Number.isNaN(parsed.getTime())) return null
    return parsed.toLocaleString()
}

const fallbackExpiry = (createdAt?: string | Date | null, durationMinutes?: number | null) => {
    if (!createdAt || typeof durationMinutes !== 'number') return null
    const createdAtDate = new Date(createdAt)
    if (Number.isNaN(createdAtDate.getTime())) return null

    const expiresAtMs = createdAtDate.getTime() + durationMinutes * 60 * 1000
    return formatExpiry(new Date(expiresAtMs).toISOString())
}

export default function PollAnswer({ answer, isUserSignedIn }: PollAnswerProps) {
    const [pendingOptionId, setPendingOptionId] = useState<string | null>(null)
    const openAuthModal = useStore(state => state.actions.modalActions.openAuthModal)

    const hasServerOptions =
        Array.isArray(answer?.poll_options_payload) && answer.poll_options_payload.length > 0
    const pollOpen = typeof answer?.poll_open === 'boolean' ? answer.poll_open : true
    const selectedOptionId = answer?.poll_user_vote_option_id ?? null
    const resultsVisible = answer?.poll_results_visible ?? !pollOpen
    const totalVotes = answer?.poll_total_votes || 0
    const expiresLabel =
        formatExpiry(answer?.poll_expires_at) ||
        fallbackExpiry(answer?.created_at, answer?.poll_duration_minutes)
    const optionsSource: PollOptionPayload[] = hasServerOptions
        ? answer.poll_options_payload || []
        : (answer?.poll_options || []).map((label: string, position: number) => ({
              id: `draft-option-${position}`,
              label,
              position,
          }))
    const options = [...optionsSource].sort(
        (left: PollOptionPayload, right: PollOptionPayload) => left.position - right.position,
    )

    const handleVote = async (pollOptionId: string, event: React.MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()

        if (!pollOpen || pendingOptionId != null || !hasServerOptions) return

        if (!isUserSignedIn) {
            openAuthModal('signup', 'poll_vote')
            return
        }

        setPendingOptionId(pollOptionId)
        try {
            await api.votePoll(answer.id, pollOptionId)
        } finally {
            setPendingOptionId(null)
        }
    }

    return (
        <>
            <div className={styles.pollCard} data-testid="poll-answer">
                <div className={styles.pollMeta}>
                    <span className={cn(styles.pollBadge, !pollOpen && styles.pollBadgeClosed)}>
                        {pollOpen ? 'Open' : 'Closed'}
                    </span>
                    <span className={styles.pollMetaSep}>·</span>
                    <span>
                        {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
                    </span>
                    {expiresLabel && (
                        <>
                            <span className={styles.pollMetaSep}>·</span>
                            <span>
                                {pollOpen ? `Closes ${expiresLabel}` : `Closed ${expiresLabel}`}
                            </span>
                        </>
                    )}
                </div>

                <div className={styles.pollOptions}>
                    {options.map(option => {
                        const isSelected = selectedOptionId === option.id
                        const percentage = option.percentage || 0
                        const votesCount = option.votes_count || 0

                        return (
                            <button
                                key={option.id}
                                type="button"
                                className={cn(
                                    styles.pollOption,
                                    isSelected && styles.pollOptionSelected,
                                    resultsVisible && styles.pollOptionResultsVisible,
                                )}
                                onClick={event => handleVote(option.id, event)}
                                disabled={!pollOpen || pendingOptionId != null || !hasServerOptions}
                            >
                                {resultsVisible && (
                                    <div
                                        className={styles.pollOptionFill}
                                        style={{
                                            width: `${Math.max(percentage, isSelected ? 2 : 0)}%`,
                                        }}
                                    />
                                )}

                                <div className={styles.pollOptionContent}>
                                    <span className={styles.pollOptionLabel} title={option.label}>
                                        {option.label}
                                    </span>
                                    {resultsVisible ? (
                                        <span className={styles.pollOptionStats}>
                                            {percentage.toFixed(1)}% · {votesCount}
                                        </span>
                                    ) : (
                                        <span className={styles.pollOptionHint}>
                                            {isUserSignedIn
                                                ? 'Vote to view results'
                                                : 'Sign in to vote'}
                                        </span>
                                    )}
                                </div>
                            </button>
                        )
                    })}
                </div>
            </div>
        </>
    )
}
